import { addToBasketV2, initAddToBasketV2 } from ".";
import { attributes, ecommerceEvents } from "../../../scripts/constants";
import {
  addClickEventToElements,
  addEventToElements,
  attribute,
  attributeAsFloat,
  attributeAsInt,
  getEventTargetAttribute,
  hasAnyElements,
  postJsonWithResponse,
  queryFirst,
  queryFirstSibling,
  trackCheckoutEvent,
  trackEcommerceEvent,
  trackEvent,
} from "../../../utils";
import { hideBasketNotificationBar } from "../alerts/hideBasketNotificationBar";
import { showTicketBasketFailure } from "../alerts/showTicketBasketFailureError";
import { updateBasketBar } from "../basket-bar/updateBasketBar";
import { endLoadingState, loadingTemplate, startLoadingState } from "../basket-v2/basketLoading";
import { errorConfirm } from "../basket-v2/errorReporting";
import { constants } from "../common";
import { isBasketPage } from "../common/isBasketPage";
import { abandonBasket } from "./api/abandonBasket";
import { getBasketData } from "./api/getBasketData";
import { ApiResponseWithData, BasketResponse } from "./api/models";
import { removeFromBasket } from "./api/removeFromBasket";
import { updateItemBasket } from "./api/updateItemBasket";
import { getBasketItemAttributes } from "./getBasketAttributes";
import { startExpiryTimer } from "./startExpiryTimer";
import { updateDropdownsFromServer } from "./updateDropdownsFromServer";

//Quick pay
import { initStripeRequestPaymentButton } from "../checkout";

const {
  basketContainer,
  basketPageIdHiddenField,
  basketUpdateAttribute,
  basketCancel,
  upgradeBasketItemAttribute,
  basketCheckout,
  basketUpgrades,
} = constants.selectors;
const basketElement = document.querySelector(basketContainer);

const errorPageHtml = `
  <section class="basket">
    <div class="section container-fluid no-padding-bottom">
      <div class="row">
          <div class="col-lg-10 offset-lg-1">
              <div class="basket-header__title-wrapper">
                  <h1 class="h1 section-title u-text-center">
                      <span>Basket Error</span>
                  </h1>
              </div>
          </div>
      </div>
      <div class="section empty container-fluid no-padding-bottom">
        <div class="col-md-5 content-wrapper text-center">
            An unexpected error occurred while trying to get the latest basket.
        </div>
      </div>
  </div>
</section>`;

const basketLoadingMessage = loadingTemplate("Loading basket");
const basketUpdatingMessage = loadingTemplate("Updating basket");
const basketRemovingMessage = loadingTemplate("Removing tickets from basket");
const basketCancellingMessage = loadingTemplate("Cancelling basket");

export async function renderBasketPageV2() {
  startLoadingState(basketLoadingMessage, ".basket-overlay");

  const basketPageId = parseInt(document.querySelector<HTMLInputElement>(basketPageIdHiddenField).value);
  const upgradeId = document.querySelector<HTMLInputElement>("#upgradeId").value;

  const response = await postJsonWithResponse(constants.urls.basketV2.content, {
    basketPageId,
    upgradeId: !!upgradeId && upgradeId.length > 1 ? parseInt(upgradeId) : null,
  });

  if (response.status != 200) {
    basketElement.innerHTML = errorPageHtml;
    window.localStorage.removeItem(constants.defaults.basketAlertLocalStorageKey);
    endLoadingState(".basket-overlay");
    return;
  }
  basketElement.innerHTML = await response.text();
  if (isUpgradePage()) {
    attachUpgradeButtons();
  } else {
    if (hasAnyElements("[data-basket]")) {
      attachBasketButtons();
      updateDropdownsFromServer();
      await startExpiryTimer();
    }
  }

  initStripeRequestPaymentButton();

  endLoadingState(".basket-overlay");

  var trackingElement = queryFirst<HTMLElement>(basketElement, `[${attributes.orderTracking}]`);
  var emptyBasketElement = queryFirst<HTMLElement>(basketElement, "#emptybasketmessage");
  
  if (trackingElement) {
    var totalPrice = attributeAsFloat(trackingElement, attributes.price);
    var trackingData = JSON.parse(attribute(trackingElement, attributes.orderTracking));

    trackEcommerceEvent({
      event: ecommerceEvents.view_cart,
      ecommerce: {
        value: totalPrice,
        items: trackingData,
      },
    });

    addClickEventToElements(`[${attributes.trackingBeginCheckout}]`, () => {
      if (!window["dataLayer"].find((x) => x.event == "begin_checkout")) {
        trackEcommerceEvent({
          event: ecommerceEvents.begin_checkout,
          ecommerce: {
            value: totalPrice,
            items: trackingData,
          },
        });
      }
    });
  }
  else if(emptyBasketElement) {
    trackCheckoutEvent({
      message_name: "basket_empty",
      message_type: "notification",
      checkout_step: "basket"      
    });  
  }
}

function attachUpgradeButtons() {
  addClickEventToElements(upgradeBasketItemAttribute, (event) => {
    event.preventDefault();
    const element = event.target as HTMLElement;
    upgradeBasketItem(element);
  });
}

function attachBasketButtons() {
  addClickEventToElements("[data-basket]", (event) => {
    const element = event.target as Element;
    removeItemFromBasket(element);
  });

  addEventToElements(`.ticket-row ${basketUpdateAttribute}`, "change", (event) => {
    const element = event.target as HTMLSelectElement;
    updateItemInBasket(element, () => renderBasketPageV2());
  });

  addClickEventToElements(basketCancel, (e) => {
    e.preventDefault();
    tryAbandonBasket();
  });

  initAddToBasketV2(() => renderBasketPageV2());

  addClickEventToElements(basketCheckout, (evt) => {
    const label = getEventTargetAttribute(evt, "data-track-label");
    trackEvent({
      event: "basket-activity",
      category: "Basket Activity",
      action: "Checkout",
      label,
    });
  });

  addClickEventToElements(basketUpgrades, () => {
    trackEvent({
      event: "basket-activity",
      category: "Basket Activity",
      action: "Show Upgrades",
      label: "",
    });
  });
}

export async function updateItemInBasket(selectionElement: HTMLSelectElement, onUpdate?: () => void) {
  const removeButton = queryFirstSibling(selectionElement.parentElement, "[data-basket]");

  if (!removeButton) {
    return;
  }

  const removalData = attribute(removeButton, "data-basket");
  if (!removalData) {
    return;
  }

  startLoadingState(basketUpdatingMessage, ".basket-overlay");

  const [eventPageId, eventId, productId] = JSON.parse(removalData);
  const selectedQuantity = parseInt(selectionElement.value);

  let response: ApiResponseWithData<BasketResponse>;
  try {
    response = await updateItemBasket({
      cmsContentId: eventPageId,
      eventId,
      productId,
      quantity: selectedQuantity,
    });
  } catch {
    endLoadingState(".basket-overlay");
    hideBasketNotificationBar();
    showTicketBasketFailure("Tickets have not been updated, please try again.", "basket_update_failed");
    reloadPage(3000);
    return;
  }

  if (!response.success) {
    var message = response.errors.length > 0 ? response.errors[0].message : "Tickets have not been updated, please try again.";
    endLoadingState(".basket-overlay");
    hideBasketNotificationBar();
    showTicketBasketFailure(message, "basket_update_failed");
    reloadPage(3000);
    return;
  }

  if (!!onUpdate) {
    await onUpdate();
  }

  const { total, totalItems, checkoutUrl } = response.data;

  updateBasketBar({ totalItems, total, checkoutUrl });
  endLoadingState(".basket-overlay");

  const itemPrice = attributeAsFloat(removeButton, attributes.price);
  const currentQuantity = attributeAsInt(removeButton, attributes.quantity);
  var trackingData = JSON.parse(attribute(removeButton, attributes.itemTracking));

  trackEcommerceEvent({
    event: ecommerceEvents.remove_from_cart,
    ecommerce: {
      value: itemPrice * currentQuantity,
      items: [{ ...trackingData, ...{ quantity: currentQuantity } }],
    },
  });

  trackEcommerceEvent({
    event: ecommerceEvents.add_to_cart,
    ecommerce: {
      value: itemPrice * selectedQuantity,
      items: [{ ...trackingData, ...{ quantity: selectedQuantity } }],
    },
  });
}

function reloadPage(delay: number) {
  setTimeout(() => {
    location.reload();
  }, delay);
}
async function removeItemFromBasket(removeButton: Element) {
  const confirmed = await errorConfirm("Are you sure you want to remove this item from your basket?");

  trackCheckoutEvent({
    message_name: "basket_remove_items_popup",    
    message_type: "notification",
    checkout_step: "basket"
  });

  if (!confirmed) {
    return;
  }

  startLoadingState(basketRemovingMessage, ".basket-overlay");

  const [eventPageId, eventId, productId] = getBasketItemAttributes(removeButton);

  let response: false | BasketResponse;
  try {
    response = await removeFromBasket({
      cmsContentId: eventPageId,
      eventId,
      productId,
    });
  } catch {
    showTicketBasketFailure("Unable to remove tickets from the basket, please try again.", "basket_remove_error");
    endLoadingState(".basket-overlay");

    trackCheckoutEvent({
      message_name: "basket_remove_error",
      message_type: "error",
      checkout_step: "basket"
    });

    return;
  }

  if (response === false) {
    showTicketBasketFailure("Unable to remove tickets from the basket, please try again.", "basket_remove_error");
    endLoadingState(".basket-overlay");
    return;
  }

  const itemPrice = attributeAsFloat(removeButton, attributes.price);
  const currentQuantity = attributeAsInt(removeButton, attributes.quantity);
  const trackingData = JSON.parse(attribute(removeButton, attributes.itemTracking));

  trackEcommerceEvent({
    event: ecommerceEvents.remove_from_cart,
    ecommerce: {
      value: itemPrice * currentQuantity,
      items: [{ ...trackingData, ...{ quantity: currentQuantity } }],
    },
  });

  trackCheckoutEvent({
    message_name: "basket_remove_confirmed",
    message_type: "notification",
    checkout_step: "basket"
  });

  await renderBasketPageV2();

  const { total, totalItems, checkoutUrl, abandoned } = response;

  if (abandoned !== true) {
    updateBasketBar({ totalItems, total, checkoutUrl });
  }

  endLoadingState(".basket-overlay");
}

async function tryAbandonBasket() {

  trackCheckoutEvent({
    message_name: "basket_cancel_order_popup",    
    message_type: "notification",
    checkout_step: "basket"
  });

  const confirm = await errorConfirm(
    "Are you sure you wish to cancel your order?  This will release any tickets you are currently holding."
  );
  if (!confirm) {
    return;
  }  

  startLoadingState(basketCancellingMessage, ".basket-overlay");

  const response = await getBasketData();
  if (!response.success) {
    showTicketBasketFailure("Unable to abandon basket, please try again.", "basket_abandon_error");
    endLoadingState(".basket-overlay");
    return;
  }

  const { total, orderItems } = response.data;
  const ticketNames = orderItems.map((o) => o.productId).join(", ");

  trackEvent({
    event: "basket-activity",
    category: "Basket Activity",
    action: "Cancel",
    label: total,
    product: ticketNames,
  });

  window.localStorage.removeItem(constants.defaults.basketAlertLocalStorageKey);

  const success = await abandonBasket();

  if (success && isBasketPage()) {

    trackCheckoutEvent({
      message_name: "basket_cancel_confirmed",    
      message_type: "notification",
      checkout_step: "basket"
    });

    location.reload();
  } else {
    endLoadingState(".basket-overlay");
  }
}

function isUpgradePage() {
  return /upgrade\/\d*\//g.test(location.pathname);
}

async function upgradeBasketItem(upgradeButton: HTMLElement) {
  startLoadingState(basketUpdatingMessage, ".basket-overlay");

  const removalData = attribute(upgradeButton, "data-basket-upgrade-params");
  const quantity = attributeAsInt(upgradeButton, "data-remove-number");
  const [
    eventPageId,
    eventId,
    productId,
    newProductId,
    dependentProducts,
  ] = JSON.parse(removalData);

await addToBasketV2(upgradeButton as HTMLElement, [
  {
    eventId,
    productId: newProductId,
    quantity,
    cmsContentId: eventPageId,
  },

  ...dependentProducts.flatMap((dependent: Record<string, number>) =>
    Object.entries(dependent).map(([productId, quantity]) => ({
      eventId,
      productId,
      quantity,
      cmsContentId: eventPageId,
    }))
  )
]);
  trackEvent({
    event: "upgrades",
    category: "Product Upgrade",
    action: upgradeButton.innerText,
    label: attribute(upgradeButton, "data-track-label"),
    productName: attribute(upgradeButton, "data-product-name"),
  });

  const success = await removeFromBasket({
    cmsContentId: eventPageId,
    eventId,
    productId,
  });

  if (!success) {
    endLoadingState(".basket-overlay");
    return;
  }

  const trackingData = JSON.parse(attribute(upgradeButton, attributes.itemUpgradeTracking));

  trackEcommerceEvent({
    event: ecommerceEvents.remove_from_cart,
    ecommerce: {
      items: trackingData,
      value: trackingData.price * quantity,
    },
  });

  location.href = (upgradeButton as HTMLLinkElement).href;
}
